import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Clock, Eye, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import Navbar from '@/components/Navbar';
import { Helmet } from 'react-helmet-async';

const API = `${window.location.origin}/api`;

export default function BlogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const page = parseInt(searchParams.get('page') || '1');
  const activeCategory = searchParams.get('category') || '';
  const searchQuery = searchParams.get('q') || '';

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ page, limit: 12 });
        if (activeCategory) params.set('category', activeCategory);
        if (searchQuery) params.set('search', searchQuery);

        const [postsRes, catsRes] = await Promise.all([
          fetch(`${API}/blog/posts?${params}`),
          fetch(`${API}/blog/categories`),
        ]);
        const postsData = await postsRes.json();
        const catsData = await catsRes.json();

        setPosts(postsData.posts || []);
        setTotal(postsData.total || 0);
        setPages(postsData.pages || 1);
        setCategories(catsData || []);
      } catch (e) {
        console.error('Blog fetch error:', e);
      }
      setLoading(false);
    };
    fetchData();
  }, [page, activeCategory, searchQuery]);

  const setCategory = (slug) => {
    const p = new URLSearchParams(searchParams);
    if (slug) p.set('category', slug);
    else p.delete('category');
    p.delete('page');
    setSearchParams(p);
  };

  const setPage = (n) => {
    const p = new URLSearchParams(searchParams);
    p.set('page', n.toString());
    setSearchParams(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const val = e.target.elements.search.value.trim();
    const p = new URLSearchParams(searchParams);
    if (val) p.set('q', val);
    else p.delete('q');
    p.delete('page');
    setSearchParams(p);
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <>
      <Helmet>
        <title>Blog - Blackified | Stories, News & Business Spotlights</title>
        <meta name="description" content="Explore stories, news, and spotlights on Black-owned businesses. Stay informed about the Blackified community." />
        <meta property="og:title" content="Blackified Blog" />
        <meta property="og:description" content="Stories, news, and spotlights on Black-owned businesses." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`${window.location.origin}/blog`} />
      </Helmet>

      <div className="min-h-screen bg-[#050505]">
        <Navbar />

        {/* Hero */}
        <header className="pt-24 pb-12 px-6">
          <div className="max-w-5xl mx-auto text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight"
            >
              The Blackified Blog
            </motion.h1>
            <p className="mt-4 text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto">
              Stories, spotlights and resources for the Black business community.
            </p>

            {/* Search */}
            <form onSubmit={handleSearch} className="mt-8 max-w-lg mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <input
                name="search"
                defaultValue={searchQuery}
                placeholder="Search articles..."
                className="w-full pl-12 pr-4 py-3 rounded-full bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-600"
                data-testid="blog-search-input"
              />
            </form>
          </div>
        </header>

        {/* Categories */}
        {categories.length > 0 && (
          <div className="px-6 pb-8">
            <div className="max-w-5xl mx-auto flex flex-wrap justify-center gap-2">
              <button
                onClick={() => setCategory('')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  !activeCategory ? 'bg-white text-black' : 'bg-zinc-900 text-zinc-400 hover:text-white border border-zinc-800'
                }`}
                data-testid="blog-cat-all"
              >
                All
              </button>
              {categories.map((c) => (
                <button
                  key={c.category_id}
                  onClick={() => setCategory(c.slug)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    activeCategory === c.slug ? 'bg-white text-black' : 'bg-zinc-900 text-zinc-400 hover:text-white border border-zinc-800'
                  }`}
                  data-testid={`blog-cat-${c.slug}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Posts grid */}
        <main className="px-6 pb-20">
          <div className="max-w-5xl mx-auto">
            {loading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="rounded-xl bg-zinc-900/50 border border-zinc-800 overflow-hidden animate-pulse">
                    <div className="h-48 bg-zinc-800" />
                    <div className="p-5 space-y-3">
                      <div className="h-4 bg-zinc-800 rounded w-1/3" />
                      <div className="h-5 bg-zinc-800 rounded w-full" />
                      <div className="h-3 bg-zinc-800 rounded w-2/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-zinc-500 text-lg">No articles found.</p>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {posts.map((post, i) => (
                    <motion.article
                      key={post.post_id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                    >
                      <Link
                        to={`/blog/${post.slug}`}
                        className="group block rounded-xl bg-zinc-900/50 border border-zinc-800 overflow-hidden hover:border-zinc-700 transition-all"
                        data-testid={`blog-post-${post.slug}`}
                      >
                        {post.featured_image ? (
                          <div className="h-48 overflow-hidden">
                            <img
                              src={post.featured_image}
                              alt={post.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              loading="lazy"
                            />
                          </div>
                        ) : (
                          <div className="h-48 bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center">
                            <span className="text-4xl font-black text-zinc-700">{post.title?.[0]}</span>
                          </div>
                        )}
                        <div className="p-5">
                          {post.category_name && (
                            <span className="text-xs font-medium text-zinc-500 uppercase tracking-wider">
                              {post.category_name}
                            </span>
                          )}
                          <h2 className="mt-1 text-base font-semibold text-white group-hover:text-zinc-200 line-clamp-2">
                            {post.title}
                          </h2>
                          <p className="mt-2 text-sm text-zinc-500 line-clamp-2">{post.excerpt}</p>
                          <div className="mt-4 flex items-center gap-4 text-xs text-zinc-600">
                            <span>{formatDate(post.published_at)}</span>
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {post.reading_time} min</span>
                            {post.views_count > 0 && (
                              <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {post.views_count}</span>
                            )}
                          </div>
                        </div>
                      </Link>
                    </motion.article>
                  ))}
                </div>

                {/* Pagination */}
                {pages > 1 && (
                  <div className="mt-12 flex items-center justify-center gap-2">
                    <button
                      disabled={page <= 1}
                      onClick={() => setPage(page - 1)}
                      className="p-2 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-400 disabled:opacity-30 hover:text-white transition-colors"
                      data-testid="blog-prev"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <span className="text-sm text-zinc-500 px-4">
                      Page {page} of {pages}
                    </span>
                    <button
                      disabled={page >= pages}
                      onClick={() => setPage(page + 1)}
                      className="p-2 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-400 disabled:opacity-30 hover:text-white transition-colors"
                      data-testid="blog-next"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>
    </>
  );
}
